
import React, { useState } from 'react';

interface LoginProps {
  onLogin: (employeeId: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    // Simulate network delay for a realistic feel
    setTimeout(() => {
      // Validate Employee ID presence
      if (!employeeNumber.trim()) {
        setError('Please enter your Employee Number.');
        setIsLoading(false);
        return;
      }

      // Hardcoded password validation as requested: 112233
      if (password === '112233') {
        setIsLoading(false);
        onLogin(employeeNumber);
      } else {
        setIsLoading(false);
        setPassword(''); // Clear password on error
        setError('Invalid credentials. Please check your password.');
      }
    }, 800);
  };

  const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>, setter: (val: string) => void) => {
    const value = e.target.value;
    // Only allow numeric input
    if (/^\d*$/.test(value)) {
      setter(value);
    }
  };

  return (
    <div className="min-h-screen bg-light-bg flex flex-col justify-center items-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="bg-white py-10 px-8 shadow-xl rounded-2xl w-full max-w-md border border-light-gray">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-8 flex flex-col items-center">
          {/* Updated Logo section */}
          <div className="text-center w-full">
            <img 
              src="https://bin.mycareconcierge.com/email/careconcierge_logo.png" 
              alt="Care Concierge Logo" 
              className="mx-auto mb-4"
              style={{height: '3rem'}}
            />
          </div>
          
          <h2 className="text-center text-3xl font-bold text-primary tracking-tight">
            Staff Login
          </h2>
          <p className="mt-3 text-center text-gray-500 text-base">
            Enter your credentials to continue.
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="employeeNumber" className="sr-only">
              Employee Number
            </label>
            <input
              id="employeeNumber"
              name="employeeNumber"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="username"
              required
              value={employeeNumber}
              onChange={(e) => handleNumericInput(e, setEmployeeNumber)}
              className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out sm:text-sm text-center font-medium tracking-widest"
              placeholder="Employee Number"
            />
          </div>

          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => handleNumericInput(e, setPassword)}
              className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 text-gray-900 bg-transparent focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition duration-150 ease-in-out sm:text-sm text-center"
              placeholder="Password"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4 border border-red-100">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading || !employeeNumber || !password}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-lg font-bold text-white bg-accent hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
